import type { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { useEffect, useMemo } from 'react';
import { Auth, useAuth } from '../AuthProvider';
import { createPreloadClient, forwardCookies } from '@zg-rentals/next-react-query5';
import { getPageFrameData, PageFrame, PageFrameComponent } from '@zillow/page-frame-next/shopper-platform';
import { useQuery } from '@zg-rentals/react-query5';
import { client } from '../rentalClient';
import { Documents, Rent, YourRentalPageLayout } from '../components/Layout';
import { Header } from '../components/Header';
import { AddRentalModal, ContactIreneModal, ViewRentalsModal } from '../components/Modals';
import { getRentals } from '../queries/getRentals';
import { useYourRentalPage } from '../hooks';
import { emitYourRentalPageView } from '../analytics/events';
import { DocumentsSkeleton, HeaderSkeleton, RentSkeleton } from '../components/Skeletons';
import { getRinsUpsell } from '../queries/getRinsUpsell';
import { getParsedZguid, hasEmptyRental } from '../utils';
import { useIsInMobileWebView } from '../hooks';

export const getServerSideProps = (async (context: GetServerSidePropsContext) => {
  const httpClient = forwardCookies(context, client);
  const ssrClient = createPreloadClient();

  const user = await ssrClient.prefetchQuery(Auth.queryOptions({ client: httpClient }));

  if (user?.isLoggedIn) {
    await ssrClient.prefetchQuery(getRentals(httpClient));
  }

  const pageFrameData = await getPageFrameData(context, {
    pfsHost: process.env.NEXT_PUBLIC_PAGE_FRAME_SERVICE,
    regionalizedLinks: false,
    useVisualRefreshTheme: true,
  });

  const ssrProps = await ssrClient.getServerSideProps();

  // Sanitize pageFrameData: Next.js cannot serialize undefined values in getServerSideProps
  // Convert undefined to null using JSON parse/stringify
  const sanitizedPageFrameData = JSON.parse(JSON.stringify(pageFrameData));

  return {
    props: {
      ...ssrProps.props,
      pageFrameData: sanitizedPageFrameData,
    },
  };
}) satisfies GetServerSideProps;

function YourRentalPageLayoutContainer({ pageFrameData }: { pageFrameData: object }) {
  const { user } = useAuth();
  const isInMobileWebView = useIsInMobileWebView();
  const isLoggedIn = user?.isLoggedIn ?? false;
  const { data: rentalsData } = useQuery({
    ...getRentals(client),
    enabled: isLoggedIn,
  });

  const parsedGuid = getParsedZguid();
  const { data: rinsUpsellData } = useQuery({
    ...getRinsUpsell(parsedGuid, client),
    enabled: !isLoggedIn && !!parsedGuid && parsedGuid.trim() !== '',
  });
  const isError = isLoggedIn && rentalsData === undefined;

  const {
    selectedProperty,
    allProperties,
    defaultProperty,
    showBreadcrumb,
    selectedTab,
    setSelectedTab,
    handlePropertyChange,
    handleAddRental,
    handleBreadcrumbClick,
    isLoadingRinsUpsellData,
  } = useYourRentalPage({
    rentalsData,
    encodedZuid: user?.encodedZuid || '',
  });

  const showSkeleton =
    (isLoggedIn && allProperties.length > 0 && selectedProperty === undefined) || isLoadingRinsUpsellData;
  const rentersInsuranceCardData = isLoggedIn
    ? selectedProperty?.rinsSectionDto?.rinsCard
    : rinsUpsellData?.renterInsuranceCard;

  useEffect(() => {
    emitYourRentalPageView();
  }, []);

  const contactIreneModal = useMemo(
    () =>
      selectedProperty?.contactSection ? (
        <ContactIreneModal contactSection={selectedProperty.contactSection} />
      ) : undefined,
    [selectedProperty?.contactSection],
  );

  const viewRentalsModal = useMemo(
    () =>
      allProperties.length > 1 && selectedProperty ? (
        <ViewRentalsModal
          yourRentalProperties={allProperties}
          selectedProperty={selectedProperty}
          onPropertyChange={handlePropertyChange}
        />
      ) : undefined,
    [allProperties, selectedProperty, handlePropertyChange],
  );

  const addRentalModal = useMemo(
    () => (!hasEmptyRental(isLoggedIn, allProperties) ? <AddRentalModal onAddRental={handleAddRental} /> : undefined),
    [isLoggedIn, allProperties, handleAddRental],
  );

  const rentTabContent = useMemo(
    () => (
      <Rent
        paymentsSection={selectedProperty?.paymentSection}
        creditReportingSection={selectedProperty?.creditReportingSection}
        listingAlias={selectedProperty?.listingSection?.listingAlias}
      />
    ),
    [
      selectedProperty?.paymentSection,
      selectedProperty?.creditReportingSection,
      selectedProperty?.listingSection?.listingAlias,
    ],
  );

  const documentsTabContent = useMemo(
    () => (
      <Documents
        leaseSection={selectedProperty?.leaseSection}
        propertyConditionSection={selectedProperty?.propertyConditionSection}
        rentersInsuranceCard={rentersInsuranceCardData}
        listingSection={selectedProperty?.listingSection}
      />
    ),
    [
      selectedProperty?.leaseSection,
      selectedProperty?.propertyConditionSection,
      rentersInsuranceCardData,
      selectedProperty?.listingSection,
    ],
  );

  const header = useMemo(
    () => (
      <Header
        isLoggedIn={isLoggedIn}
        listingSection={selectedProperty?.listingSection}
        hasMultipleRentals={allProperties.length > 1}
        showBreadcrumb={showBreadcrumb}
        breadcrumbAddress={defaultProperty?.listingSection?.address}
        contactIreneModal={contactIreneModal}
        viewRentalsModal={viewRentalsModal}
        addRentalModal={addRentalModal}
        onBreadcrumbClick={handleBreadcrumbClick}
      />
    ),
    [
      isLoggedIn,
      selectedProperty?.listingSection,
      allProperties.length,
      showBreadcrumb,
      defaultProperty?.listingSection?.address,
      contactIreneModal,
      viewRentalsModal,
      addRentalModal,
      handleBreadcrumbClick,
    ],
  );

  if (isInMobileWebView) {
    return (
      <YourRentalPageLayout
        header={showSkeleton ? <HeaderSkeleton /> : header}
        rentTabContent={showSkeleton ? <RentSkeleton /> : rentTabContent}
        documentsTabContent={showSkeleton ? <DocumentsSkeleton /> : documentsTabContent}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        isError={isError}
      />
    );
  }

  return (
    <PageFrame
      pageFrameData={pageFrameData}
      components={[
        PageFrameComponent.TOPNAV,
        PageFrameComponent.FOOTER,
        PageFrameComponent.BODYSCRIPTS,
        PageFrameComponent.REGLOGIN,
      ]}
    >
      <YourRentalPageLayout
        header={showSkeleton ? <HeaderSkeleton /> : header}
        rentTabContent={showSkeleton ? <RentSkeleton /> : rentTabContent}
        documentsTabContent={showSkeleton ? <DocumentsSkeleton /> : documentsTabContent}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        isError={isError}
      />
    </PageFrame>
  );
}

export default YourRentalPageLayoutContainer;
